import React, { useState } from "react"

import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@coval/ui"
import { MessageSquare, Plus, X } from "lucide-react"

import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType, type TestSetInput } from "@/types/TestSetInput"
import { cn } from "@/utils/cn"

type Role = "User" | "Agent"

interface Turn {
  role: Role
  text: string
}

interface TranscriptModalProps {
  onTestCasesProcessed: (testSetInput: TestSetInput) => Promise<void>
  triggerClassName?: string
}

const TranscriptModal: React.FC<TranscriptModalProps> = ({
  onTestCasesProcessed,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [turns, setTurns] = useState<Turn[]>([{ role: "User", text: "" }])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetState = () => {
    setTurns([{ role: "User", text: "" }])
    setIsSubmitting(false)
  }

  const updateTurnText = (index: number, text: string) => {
    const updated = [...turns]
    updated[index] = { ...updated[index], text }
    setTurns(updated)
  }

  const updateTurnRole = (index: number, role: Role) => {
    const updated = [...turns]
    updated[index] = { ...updated[index], role }
    setTurns(updated)
  }

  const addTurn = () => {
    const lastRole = turns[turns.length - 1]?.role ?? "Agent"
    const nextRole: Role = lastRole === "User" ? "Agent" : "User"
    setTurns((prev) => [...prev, { role: nextRole, text: "" }])
  }

  const removeTurn = (index: number) => {
    setTurns((prev) => prev.filter((_, i) => i !== index))
  }

  const hasValidTurns = turns.some((t) => t.text.trim().length > 0)

  const handleSubmit = async () => {
    if (!hasValidTurns) {
      return
    }

    setIsSubmitting(true)
    try {
      const nonEmptyTurns = turns.filter((t) => t.text.trim().length > 0)
      const inputStr = nonEmptyTurns
        .map((t) => `${t.role}: ${t.text.trim()}`)
        .join("\n")

      const testCases: TestCaseInput[] = [
        {
          input_str: inputStr,
          expected_behaviors: [],
          s3_key: null,
          s3_bucket: null,
          input_type: TestSetType.TRANSCRIPT,
        },
      ]

      await onTestCasesProcessed({
        display_name: "",
        description: "Transcript test cases",
        test_set_metadata: { owner: "" },
        testCases,
        test_set_type: TestSetType.TRANSCRIPT,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Card
        interactive
        className={cn("flex items-center gap-3 px-4 py-3", triggerClassName)}
        onClick={() => setIsOpen(true)}
      >
        <MessageSquare className="w-4 h-4 text-coval-text-muted flex-shrink-0" />
        Write a transcript conversation
      </Card>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            resetState()
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Create Transcript Test Case</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <p className="text-sm text-coval-text-muted">
                Write a reference conversation between User and Agent. The
                simulated user will adapt as needed during the simulation.
              </p>

              {turns.map((turn, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Select
                    value={turn.role}
                    onValueChange={(value) =>
                      updateTurnRole(index, value as Role)
                    }
                  >
                    <SelectTrigger
                      className="min-w-[90px] max-w-[90px] h-10"
                      aria-label={`Role for turn ${index + 1}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="User">User</SelectItem>
                      <SelectItem value="Agent">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    aria-label={`Turn ${index + 1} content`}
                    placeholder={`What the ${turn.role.toLowerCase()} says...`}
                    value={turn.text}
                    onChange={(e) => updateTurnText(index, e.target.value)}
                    className="flex-1 min-h-[56px]"
                  />
                  {turns.length > 1 && (
                    <Button
                      icon
                      variant="ghost"
                      className="mt-1.5"
                      onClick={() => removeTurn(index)}
                      tooltip={`Remove turn ${index + 1}`}
                    >
                      <X className="w-4 h-4 text-coval-text-muted" />
                    </Button>
                  )}
                </div>
              ))}

              <Button variant="secondary" onClick={addTurn}>
                <Plus className="w-4 h-4" />
                Add Turn
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void (async () => {
                  try {
                    await handleSubmit()
                    setIsOpen(false)
                  } catch {
                    // Parent surfaces errors via toast; keep modal open on failure
                  }
                })()
              }}
              disabled={!hasValidTurns || isSubmitting}
            >
              Add test case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TranscriptModal
